export default {
  app: {
    name: 'Manufacturing'
  },
  brand: {
    // Lime's mission statement, verbatim. Public material — it is on Lime's own brand site — so it is
    // safe on a screen that supplier operators also see.
    mission: 'Build a future where transportation is shared, affordable and carbon-free.'
  },
  nav: {
    section: {
      vehicle: 'Vehicle',
      ccu: 'CCU',
      tools: 'Station software',
      firmware: 'Firmware',
      codes: 'Codes',
      admin: 'Administration'
    },
    dashboard: 'Dashboard',
    flashRecords: 'Flash records',
    acceptanceRecords: 'Acceptance records',
    ccuIdentity: 'CCU identity',
    ccuDevices: 'Accepted modules',
    shipmentCheck: 'CCU list check',
    shipmentManifests: 'CCU list checks',
    vehicleBindings: 'Bindings',
    vehicleCommands: 'Vehicle commands',
    checkItems: 'Check items',
    standards: 'Acceptance standards',
    vehicleModels: 'Vehicle models',
    skus: 'SKU management',
    toolDownloads: 'Downloads',
    toolReleases: 'Releases',
    firmwareDownloads: 'Downloads',
    firmwareReleases: 'Releases',
    stockIssuance: 'Issue codes',
    legacyCodes: 'Legacy codes',
    organisations: 'Factories',
    roles: 'Roles',
    operators: 'Operators',
    chirpCredential: 'Vehicle credential',
    sops: 'SOPs',
    sop: 'SOP',
    openMenu: 'Open navigation',
    closeMenu: 'Close navigation'
  },
  chirp: {
    title: 'Vehicle credential',
    subtitle: 'What this service uses to reach vehicles. Developer credentials are minted here, each with the minting administrator\'s own account; the factory credential is the Chirp service account\'s, which the server signs in and keeps signed in. When that fails, sign it in here.',
    loadFailed: 'The credential status could not be read.',
    session: 'Chirp service account',
    sessionSignedIn: 'Signed in as {account}',
    sessionNone: 'Not signed in',
    sessionBy: 'Signed in by',
    sessionAutomatic: 'the server, automatically',
    sessionManual: '{name}, from a browser',
    sessionUntil: 'Valid until (UTC)',
    sessionNoPassword: 'No password is configured for the service account here, so it can only be signed in by hand.',
    sessionFailed: 'The last automatic sign-in failed ({at}): {reason}',
    factoryUntil: 'Factory credential valid until (UTC)',
    factoryNone: 'Not issued yet. It is issued the first time a vehicle inside its factory window is reached.',
    signIn: 'Sign in by hand',
    signInHint: 'Opens the Cloudflare sign-in. Use a private window and sign in as the Chirp service account, not as yourself.',
    signedIn: 'Signed in. The server carries on with this session.',
    inUse: 'In use',
    none: 'No credential is in use. Vehicles no credential covers can only be reached through the one this deployment is configured with, if any.',
    role: {
      developer: 'Developer credential',
      factory: 'Factory credential'
    },
    vehicles: 'Vehicles covered',
    factoryVehicles: 'Every vehicle inside its factory window, which starts when it is registered with Chirp',
    expiresAt: 'Expires (UTC)',
    mintedBy: 'Minted by',
    handle: 'Revocation handle',
    revoke: 'Revoke now',
    revoked: 'Revoked. The vehicles it covered cannot be reached until a credential covering them is minted.',
    minted: 'Credential minted and in use.',
    interrupted: 'This was interrupted before it finished, and no credential was minted. Try again.',
    waitingTitle: 'Waiting for sign-in',
    waiting:
      'A tab has been opened for you to sign in: as yourself when minting, as the service account when signing it in by hand. This page updates itself when it completes, so leave it open. If the tab did not open, use the button below.',
    openAgain: 'Open the sign-in again',
    minting: 'Signed in. Minting.',
    failedTitle: 'The last attempt did not work',
    quotaAdvice: 'Your account already has as many live developer credentials as it may hold. Revoke one of yours and try again.',
    mintFirst: 'Mint a credential',
    mintAnother: 'Add a credential for more vehicles',
    imeiPlaceholder: 'One IMEI per line',
    imeiHint: '{count} vehicle(s) entered. Only for vehicles outside their factory window; those inside it are covered by the factory credential.',
    mint: 'Mint',
    mintHint: 'Minting opens a sign-in page: sign in with your own email. The credential counts against your own quota; the service account only looks after the factory credential.',
    account: 'Account'
  },
  product: {
    category: {
      S: 'Scooter',
      E: 'Ebike',
      G: 'Glider'
    },
    models: {
      title: 'Vehicle models',
      subtitle: 'The projects a SKU can be a build of.',
      create: 'New model',
      createTitle: 'New vehicle model',
      editTitle: 'Edit vehicle model',
      name: 'Model',
      code: 'Project code',
      category: 'Category',
      frameSegment: 'Frame positions 4-6',
      frameSegmentPlaceholder: 'e.g. 046',
      frameSegmentInvalid: 'Exactly three digits, e.g. 046. A leading zero matters.',
      framePreview: 'A frame number of this model, assembled at factory {factory}, reads:',
      description: 'Description',
      state: 'State',
      stateEnabled: 'In use',
      stateDisabled: 'Retired',
      nameRequired: 'A model name is required.',
      codeRequired: 'A project code is required.',
      codeLocked: 'The project code cannot be changed: {count} SKUs are builds of this model and refer to it by code.',
      actions: 'Actions',
      edit: 'Edit',
      enable: 'Put in use',
      disable: 'Retire',
      enabled: 'This model is in use again.',
      disabled: 'This model has been retired.',
      disableConfirm: 'Retire {name}? No new SKU will be able to name it. Its {count} existing SKUs and everything produced under them are unaffected.',
      disableConfirmTitle: 'Retire this model',
      cancel: 'Cancel',
      save: 'Save',
      saved: 'Saved.',
      empty: 'No vehicle models yet.',
      loadFailed: 'The vehicle models could not be loaded.'
    },
    powerType: {
      PEDAL_ONLY: 'Pedal only',
      THROTTLE_ONLY: 'Throttle only',
      PEDAL_AND_THROTTLE: 'Pedal and throttle'
    },
    skus: {
      title: 'SKU management',
      subtitle: 'What a vehicle of each model is expected to be built from.',
      create: 'New SKU',
      createTitle: 'New SKU',
      editTitle: 'Edit SKU',
      search: 'Search',
      searchPlaceholder: 'SKU code or description',
      anyState: 'Any state',
      stateEnabled: 'In use',
      stateDisabled: 'Retired',
      standard: 'Acceptance standard',
      standardNone: 'None chosen',
      standardDraft: 'editable',
      standardTitle: 'Acceptance standard for {code}',
      standardHint: 'Choose a configured standard version. One marked editable has not judged a vehicle yet, so it can still change; the first vehicle tested under it is what settles it.',
      standardItemCount: '{count} checks',
      standardEmpty: 'No standards have been configured yet.',
      standardLoadFailed: 'The standards could not be loaded.',
      standardSaved: 'Acceptance standard updated.',
      configureStandard: 'Acceptance standard',
      componentWithdrawn: 'retired',
      qrCaption: 'Printed and scanned at binding.',
      model: 'Model',
      anyModel: 'Any model',
      modelRequired: 'Choose the model this SKU is a build of.',
      code: 'SKU',
      codePlaceholder: 'LPN-XXXXXX',
      descriptionExampleLabel: 'For example:',
      descriptionExample: 'Asy, Parrot Ebike E5.1, Israel',
      description: 'Description',
      maxSpeed: 'Configured speed',
      maxSpeedPlaceholder: 'e.g. 25',
      maxSpeedInvalid: 'A configured speed has to be a number above zero. Leave it empty where this model has no limit.',
      kph: 'km/h',
      powerType: 'Drive',
      notSpecified: 'Not specified',
      vin: 'VIN',
      requiresVin: 'This model carries a VIN',
      vinYes: 'Carried',
      vinNo: 'Not carried',
      state: 'State',
      components: 'Parts',
      configuration: 'Configuration',
      expectedBuild: 'Expected build',
      buildHint: 'Everything is ticked to start with — untick the parts this model does not have. The list is saved whole, so unticking one removes it. Which versions of a part are acceptable belongs to the acceptance standard, not here.',
      noComponents: 'No parts listed yet.',
      codeRequired: 'A SKU code is required.',
      actions: 'Actions',
      edit: 'Edit',
      enable: 'Put in use',
      disable: 'Retire',
      enabled: 'This model is in use again.',
      disabled: 'This model has been retired.',
      disableConfirm: 'Retire {code}? A new production order will not be able to use it. Nothing already produced changes.',
      disableConfirmTitle: 'Retire this SKU',
      cancel: 'Cancel',
      save: 'Save',
      saved: 'Saved.',
      empty: 'No SKUs match.',
      loadFailed: 'The SKUs could not be loaded.',
      detailLoading: 'Loading the expected build…',
      detailFailed: 'The expected build could not be loaded.'
    }
  },
  login: {
    heading: 'Sign in',
    subheading: 'Internal staff sign in with Google.',
    googleUnconfigured:
      'Google sign-in is not configured in this environment. Set VITE_GOOGLE_CLIENT_ID to enable it.',
    googleUnavailable: 'Google sign-in could not be loaded. Check the network and try again.',
    supplierToggle: 'Supplier sign-in',
    supplierHint: 'For factory accounts that cannot use Google.',
    username: 'Username',
    password: 'Password',
    rememberMe: 'Keep me signed in on this station',
    submit: 'Sign in',
    backToGoogle: 'Back to Google sign-in',
    signingIn: 'Signing in…'
  },
  session: {
    signOut: 'Sign out',
    expired: 'Your session has expired. Please sign in again.',
    role: 'Role',
    permissions: 'Permissions'
  },
  dashboard: {
    heading: 'Dashboard',
    subtitle: 'What each station produced, how its yield moved, and what needs a person.',
    station: {
      ccu: 'CCU EOL',
      ccuScope:
        'CCU modules only, counted from every attempt recorded at the two CCU stations. Nothing here describes a whole vehicle.',
      vehicle: 'Vehicle EOL',
      vehicleScope: 'Whole vehicles, once the vehicle stations report into this service.'
    },
    vehicle: {
      kpi: {
        vehicles: 'Vehicles tested',
        vehiclesHint: 'Distinct vehicles. {attempts} attempts behind them, and a vehicle taking both inspections is counted once.',
        aborted: 'Abandoned',
        abortedHint: 'The attempt stopped without a verdict. Neither a defect nor an outage.'
      },
      waiting: {
        title: 'Still waiting',
        hint: 'Vehicles standing somewhere, not rates. Counted once each, however many times they failed.',
        awaitingRetest: 'Awaiting retest',
        suspectedMisBindings: 'Possible mis-binding',
        misBindingHint:
          'A possible mis-binding is an operator acting on the vehicle and the vehicle staying silent. It is counted apart from the components on purpose: recorded as a component fault it sends a working part to rework and leaves the wrong pairing on the line.'
      },
      testType: {
        STATIC: 'Static inspection',
        RIDE: 'Ride test'
      },
      trendHint:
        'Both inspections together. A ride test only follows a static pass, so splitting the line would invite reading the lag as a drop. Never add this to the CCU trend.',
      checkParetoHint: 'Which check failed most. What to fix.',
      componentPareto: {
        title: 'Failures by component',
        hint: 'Which part the failures sit on. Who to talk to. A component total includes checks that fell below the per-check cut, so these do not add up to the list above.'
      },
      paretoDetail: '{failures} failures · {vehicles} vehicles · {share} of the window',
      noComponent: 'No component'
    },
    window: {
      days: '{days} days'
    },
    windowCaption: '{from} to {to}, UTC',
    refresh: 'Refresh',
    loadFailed: 'These figures could not be loaded.',
    retry: 'Try again',
    noFigures:
      'This account has no production figures to read. The sidebar shows the pages it can reach.',
    kpi: {
      modules: 'Modules tested',
      modulesHint: '{attempts} attempts, retests included',
      firstPassYield: 'First pass yield',
      firstPassYieldHint: '{passes} of {attempts} passed at the first attempt',
      rejected: 'Rejected',
      rejectedHint: 'Attempts where the module did not meet the standard',
      infraErrors: 'Interrupted',
      infraErrorsHint: 'Stopped by something that is not the module, so never counted as a defect'
    },
    trend: {
      title: 'First pass yield',
      hint: 'Bars are attempts a day, the line is first pass yield. A gap is a day nothing was tested, and today is a partial day — so the last point is normally the lowest.',
      attempts: 'Attempts',
      yield: 'First pass yield',
      empty: 'No CCU module was tested in this window.'
    },
    stages: {
      OUTGOING: 'Outgoing, at the module supplier',
      INCOMING: 'Incoming, at the assembly plant',
      yield: 'First pass yield',
      modules: 'Modules',
      attempts: 'Attempts',
      rejected: 'Rejected'
    },
    pareto: {
      title: 'What is failing',
      hint: 'The CCU checks that failed most. A check whose value could not be read at all is not counted here — that is a station problem rather than a defect of the module.',
      failures: 'Failures',
      detail: '{failures} failures · {modules} modules · {share} of all failures',
      empty: 'No check failed in this window.'
    },
    identity: {
      title: 'CCU cloud identity registration',
      hint: 'Collected at the CCU stations and nowhere else. Not limited to the window above: a backlog does not stop being one as it ages.',
      pending: 'Waiting',
      inProgress: 'In progress',
      registered: 'Registered',
      failed: 'Given up',
      oldestPending: 'The oldest one has been waiting since {at} UTC.',
      nothingPending: 'Nothing is waiting.'
    },
    attention: {
      title: 'Needs attention',
      clear: 'Nothing needs attention.',
      identityFailed:
        '{count} modules have used up their registration attempts. They will not reach the cloud until someone looks.',
      identityReplaced:
        '{count} old keys registered with Chirp were replaced and not revoked. The cloud may still hold them, and their CCUs cannot connect with their new keys.',
      identityStuck:
        '{count} registrations have been claimed and not answered for. A figure that does not move is a worker that stopped mid-flight.',
      identityDuplicate:
        '{count} attempts were rejected because another module already holds one of the same identity values. One of the two modules is misidentified.'
    },
    serverBuild: 'Server build',
    serverTime: 'Server time (UTC)'
  },
  placeholder: {
    title: 'This page has no backend behind it yet.',
    willContain: 'It will contain'
  },
  scanCode: {
    alt: 'Scannable symbol for {value}',
    empty: 'Nothing to encode',
    cannotRender: 'This value cannot be drawn as a barcode',
    dialogHint: 'Point a scanner at the screen, or read the value under each symbol.',
    noCodes: 'This row carries no code that can be scanned.',
    show: 'Codes'
  },
  ops: {
    command: {
      title: 'Vehicle commands',
      description:
        'Every command an operator has sent a vehicle from a bench: unlock, lock, release the battery lock, read the firmware version. The vehicle keeps no account anybody here can read, which is why this is kept — so "who unlocked this vehicle" has an answer. Every command is listed whatever became of it, including the ones that never left the service. Sent, not confirmed means nothing reported the vehicle doing it; it is not the same as the vehicle refusing, and it is not a fault to send anything to rework over.',
      search: 'Search',
      retry: 'Try again',
      loadFailed: 'The commands could not be loaded.',
      empty: 'No commands match.',
      imei: 'Module',
      imeiPlaceholder: 'IMEI starts with',
      frameNumberPlaceholder: 'Frame number starts with',
      plateNumberPlaceholder: 'Plate',
      vehicle: 'Vehicle',
      notBoundYet: 'Not bound yet',
      operator: 'Sent by',
      sentAt: 'Sent',
      confirmedAt: 'Confirmed',
      commandColumn: 'Command',
      outcomeColumn: 'Outcome',
      transportColumn: 'Reached by',
      subject: 'Subject',
      commandId: 'Command id',
      said: 'What the vehicle said',
      saidNothing: 'The vehicle said nothing in the window.',
      still: 'AI box picture',
      stillFailed: 'The picture could not be loaded.',
      stillTaken: 'Taken by the command sent at {time}. If it does not match the scene, the box may have kept an older picture.',
      errorDetail: 'What the transport reported',
      filterAnyCommand: 'Any command',
      filterAnyOutcome: 'Any outcome',
      filterAnyTransport: 'Reached any way',
      command: {
        UNLOCK: 'Unlock',
        LOCK: 'Lock',
        BATTERY_SWAP: 'Release the battery lock',
        READ_FIRMWARE: 'Read firmware version',
        OPS_ENTER: 'Enter OPS',
        OPS_EXIT: 'Exit OPS',
        REBOOT: 'Reboot CCU',
        RING: 'Ring',
        FOTA: 'Start OTA',
        FOTA_CANCEL: 'Cancel OTA',
        OPS_ECU_POWER_ON: 'ECU power on',
        OPS_ECU_POWER_OFF: 'ECU power off',
        OPS_ECU_LOCK: 'ECU lock',
        OPS_ECU_UNLOCK: 'ECU unlock',
        OPS_ECU_HEADLIGHT_ON: 'Headlight on',
        OPS_ECU_HEADLIGHT_OFF: 'Headlight off',
        OPS_ECU_UPGRADE: 'Flash ECU',
        OPS_CAN_SEND: 'Send CAN frame',
        OPS_AUDIO_TEST_SOUND: 'Test sound',
        OPS_BTL_LOCK: 'Battery lock: lock',
        OPS_BTL_UNLOCK: 'Battery lock: unlock',
        OPS_HUBLOCK_LOCK: 'Hub lock: lock',
        OPS_HUBLOCK_UNLOCK: 'Hub lock: unlock',
        WIFI_PROVISION: 'Add WiFi',
        WIFI_FORGET: 'Forget WiFi',
        OPS_VPU_CAPTURE_IMAGE: 'AI box: capture and show the picture',
        VPU_CAPTURE_IMAGE: 'AI box: capture',
        VPU_DIAGNOSTIC: 'AI box: diagnostic'
      },
      group: {
        TRIP: 'Trip',
        OPS_MODE: 'OPS mode',
        ECU: 'ECU',
        BATTERY_LOCK: 'Battery lock',
        HUB_LOCK: 'Hub lock',
        SYSTEM: 'CCU',
        FIRMWARE: 'Firmware',
        NETWORK: 'Network',
        AI_BOX: 'AI box'
      },
      argument: {
        operatorToken: 'Operator token',
        taskToken: 'Task token',
        delaySeconds: 'Delay (s)',
        pattern: 'Pattern',
        durationMs: 'Duration (ms)',
        imageUrl: 'Image URL',
        sha256: 'SHA-256',
        targetCommandId: 'Command id to cancel',
        path: 'Path on CCU',
        canId: 'CAN ID (hex)',
        data: 'Data (hex)',
        extended: 'Extended frame',
        sound: 'Clip file',
        volumePct: 'Volume %',
        ssid: 'SSID',
        passphrase: 'Password',
        operation: 'Operation'
      },
      outcome: {
        CONFIRMED: 'Done',
        NOT_CONFIRMED: 'Sent, not confirmed',
        PUBLISHED: 'Sent (no feedback)',
        INFRA_ERROR: 'Not sent'
      },
      transport: {
        CLOUD_CHIRP: 'Over the network',
        STATION_LOCAL_BUS: 'From a cabled bench'
      },
      failure: {
        TRANSPORT_UNAVAILABLE: 'Vehicles could not be reached',
        RATE_LIMITED: 'Refused for volume'
      }
    },
    state: {
      group: {
        MODE: 'Mode',
        CCU: 'CCU',
        ECU: 'ECU',
        BMS: 'Battery',
        BATTERY_LOCK: 'Battery lock',
        HUB_LOCK: 'Hub lock',
        DISPLAY: 'Display',
        QI_CHARGER: 'Qi charger',
        NETWORK: 'Network',
        GPS: 'GPS',
        AI_BOX: 'AI box',
        AUDIO: 'Audio',
        SENSOR: 'IMU'
      }
    }
  },
  provision: {
    records: {
      title: 'CCU5 flash records',
      description:
        'One row per attempt at a flash station: the firmware a bare board was given, and whether the production key hash was fused into it and locked. Both halves are one-way and a closed board cannot be read back, so this is the only account of what happened to a board — a failed attempt is kept for the same reason a successful one is. Fusing is a further deliberate act, so an attempt that stopped after flashing is the ordinary case and not a defect.',
      search: 'Search',
      batchPlaceholder: 'Batch starts with',
      machinePlaceholder: 'Board revision',
      operatorPlaceholder: 'Operator',
      filterAnyFlash: 'Any flash outcome',
      filterAnyClose: 'Any close outcome',
      filterAnyFuse: 'Any fuse outcome',
      batch: 'Batch',
      machine: 'Board revision',
      firmware: 'Firmware',
      flashColumn: 'Flash',
      closeColumn: 'Close',
      fuseColumn: 'Fuse',
      failureColumn: 'Failure',
      locked: 'Locked',
      notLocked: 'Not locked',
      where: 'Where and who',
      startedAt: 'Started',
      finishedAt: 'Finished',
      unfinished: 'Abandoned',
      recordedAt: 'Recorded',
      whatWasWritten: 'What was written, and what was read back',
      bundleName: 'Bundle',
      noBundleName: 'Not reported',
      programmerVersion: 'Programmer version',
      firmwareDigest: 'Bundle digest',
      noDigest: 'Not computed',
      otpReadBack: 'Key hash read back',
      otpNeverRead: 'Never read',
      boardIdentifier: 'Board identifier',
      noBoardIdentifier: 'Nothing identified this board',
      toolVersion: 'Tool version',
      attemptRef: 'Attempt reference',
      noAttemptRef: 'Not reported',
      failuresOnThisPage: '{count} attempts failed on this page',
      empty: 'No attempts recorded yet. A flash station creates these as it works.',
      loadFailed: 'Could not load the flash records.',
      retry: 'Try again',
      flash: {
        OK: 'Written',
        FAILED: 'Failed',
        NOT_ATTEMPTED: 'Not attempted'
      },
      close: {
        OK: 'Closed',
        FAILED: 'Failed',
        REFUSED: 'Refused',
        NOT_ATTEMPTED: 'Not attempted'
      },
      fuse: {
        OK: 'Fused',
        FAILED: 'Failed',
        REFUSED: 'Refused',
        NOT_ATTEMPTED: 'Not attempted'
      }
    }
  },
  ccuEol: {
    checkItems: {
      title: 'CCU check items',
      description:
        'Every check this service knows how to run on a CCU module. The acceptance standard of a batch picks from this list and sets the rule for each one.',
      itemSetManagedByMigration:
        'The list of checks is not edited here. An item exists because the code knows how to read it from a module, so a new one arrives with a migration reviewed alongside that code. The channel is the one thing set on this page.',
      code: 'Code',
      name: 'Name',
      channel: 'Channel',
      reads: 'What it reads',
      state: 'State',
      enabled: 'Enabled',
      disabled: 'Disabled',
      noDescription: 'No description yet',
      channelNotEstablished: 'Not established',
      channelPlaceholder: 'e.g. CAN',
      editChannelMap: 'Set channels',
      saveChannelMap: 'Save channels',
      cancel: 'Cancel',
      channelMapSaved: 'Channels saved.',
      groupsLabel: 'Reads at the same time:',
      groupCount: '{count} checks',
      groupsHint:
        'Checks on the same channel are read one after another; different channels are read at the same time. Checks with no channel established are all read one at a time, so filling one in only ever shortens the test.',
      empty: 'No check items. Run the migrations against this database.',
      loadFailed: 'Could not load the check items.',
      retry: 'Try again',
      count: '{count} items'
    },
    acceptanceRecords: {
      title: 'CCU EOL acceptance records',
      description:
        'One row per attempt at a CCU acceptance station. A retest is its own row and the earlier one is kept, so a module that failed and passed on retest appears twice — first pass yield counts only first attempts. Expand a row for every check, the value the module reported, and the rule it was judged against at that moment.',
      search: 'Search',
      filterAnyBatch: 'Any batch',
      filterAnyStage: 'Any stage',
      filterAnyVerdict: 'Any verdict',
      imeiPlaceholder: 'IMEI starts with',
      firstAttemptOnly: 'First attempts only',
      imei: 'IMEI',
      batch: 'Batch',
      batchMissing: 'Batch missing',
      stageColumn: 'Stage',
      attemptNo: 'Attempt',
      verdictColumn: 'Verdict',
      failedItems: '{count} failed',
      released: 'Released',
      releasedYes: 'Yes',
      releasedNo: 'Not yet',
      where: 'Where and who',
      startedAt: 'Started',
      finishedAt: 'Finished',
      unfinished: 'Not finished',
      toolVersion: 'Tool version',
      collected: 'What this attempt read from the module',
      iccid: 'SIM ICCID',
      bluetoothName: 'Bluetooth name',
      bluetoothMac: 'Bluetooth MAC',
      wifiMac: 'WiFi MAC',
      publicKey: 'Cloud identity public key',
      checks: 'Checks',
      item: 'Check',
      rawValue: 'Reported',
      noValue: 'No value obtained',
      judgedAgainst: 'Judged against',
      execution: 'Read',
      itemVerdict: 'Verdict',
      attempts: 'This module at this stage — {count} attempts',
      detailLoading: 'Loading…',
      detailFailed: 'Could not load this attempt.',
      firstAttemptFailures: '{count} first attempts failed on this page',
      empty: 'No attempts recorded yet. A station tool creates these when it reports a test.',
      loadFailed: 'Could not load the acceptance records.',
      retry: 'Try again',
      stage: {
        OUTGOING: 'Outgoing',
        INCOMING: 'Incoming'
      },
      verdict: {
        PASS: 'Pass',
        FAIL: 'Fail',
        INFRA_ERROR: 'Infrastructure'
      },
      execution_: {
        OK: 'OK',
        TIMEOUT: 'Timeout',
        ERROR: 'Error',
        SKIPPED: 'Skipped'
      },
      itemVerdict_: {
        PASS: 'Pass',
        FAIL: 'Fail',
        NOT_JUDGED: 'Recorded'
      },
      rule: {
        exact: 'Must equal {value}',
        regex: 'Must match {pattern}',
        range: 'Between {min} and {max}',
        none: 'Recorded, not judged'
      }
    },
    identity: {
      title: 'CCU identity',
      description:
        'The CCU module identity collected at the only station that collects it: IMEI, SIM ICCID, Bluetooth MAC and broadcast name, WiFi MAC, and the cloud-identity public key. These belong to the CCU module, not to a whole vehicle, and there is no systematic chance to collect them again once the unit ships.',
      search: 'Search',
      imeiPlaceholder: 'IMEI starts with',
      iccidPlaceholder: 'ICCID starts with',
      filterAnyStatus: 'Any state',
      status: {
        PENDING: 'Waiting to register',
        IN_PROGRESS: 'Registering',
        REGISTERED: 'Registered',
        FAILED: 'Registration failed'
      },
      imei: 'IMEI',
      iccid: 'SIM ICCID',
      statusColumn: 'Cloud registration',
      attemptCount: '{count} attempt(s)',
      where: 'Collected at',
      collectedAt: 'Collected',
      registeredAt: 'Registered',
      notRegistered: 'Not yet',
      unregistered: '{count} of the modules listed cannot reach the cloud yet',
      empty: 'No identities yet. A module gets one when it passes outgoing or incoming inspection.',
      loadFailed: 'Could not load the identities.',
      retry: 'Try again',
      detailLoading: 'Loading…',
      detailFailed: 'Could not load this identity.',
      collected: 'What the inspection read',
      bluetoothName: 'Bluetooth name',
      bluetoothMac: 'Bluetooth MAC',
      wifiMac: 'WiFi MAC',
      stage: 'Station',
      stage_: {
        OUTGOING: 'Outgoing (CCU factory)',
        INCOMING: 'Incoming (assembly)'
      },
      operator: 'Operator',
      publicKey: 'Cloud public key',
      registration: 'Registering it',
      attempts: 'Attempts used',
      nextAttemptAt: 'Next attempt',
      remoteRef: 'Registry reference',
      lastError: 'Last failure',
      keyHistory: 'Replaced keys ({count})',
      replacedKey: 'Replaced key',
      reachedState: 'Before it was replaced',
      replacedKeyState: {
        never: 'Never registered',
        registered: 'Was registered',
        revoked: 'Revoked'
      },
      revokedBy: 'Revoked by {name} at {at} (UTC)',
      revokedAt: 'Revoked no later than {at} (UTC)',
      supersededAt: 'Replaced',
      registryStale:
        'An old key of this CCU was registered with Chirp before its key changed. The cloud may still hold the old key, and the CCU cannot connect with its new one until it is replaced. Use “Register with Chirp” and confirm, to revoke the old key and register the new one.',
      register: 'Register with Chirp',
      registerHint: 'Registering lets the vehicle connect to Chirp, and opens its one factory window from that moment.',
      registered: 'Registered with Chirp.',
      replaceTitle: 'Revoke the old key',
      replaceConfirm: 'Chirp still holds the key this CCU reported before. Registering the new key revokes the old one in Chirp first, and that is permanent: the old key can never be registered again, under any IMEI. Continue?',
      replaceConfirmButton: 'Revoke and register',
      cancel: 'Cancel'
    },
    device: {
      title: 'Accepted CCU modules',
      description:
        'One row per module that passed incoming inspection, and whether the ops platform has been told about it. These are the values it is called by — IMEI, SIM ICCID, Bluetooth and WiFi — as they stood when the module was accepted, which is what was sent rather than what the latest test happened to read. Market, manufacturer and modem stay blank until their mappings are agreed.',
      search: 'Search',
      imeiPlaceholder: 'IMEI starts with',
      iccidPlaceholder: 'ICCID starts with',
      filterAnyStatus: 'Any state',
      filterAnySource: 'Any origin',
      status: {
        PENDING: 'Waiting to send',
        IN_PROGRESS: 'Sending',
        SYNCED: 'Sent',
        FAILED: 'Sending failed',
        WITHHELD: 'Held back'
      },
      sourceColumn: 'How it got here',
      source: {
        INSPECTION: 'Inspected',
        MANUAL_ADMISSION: 'Admitted by hand'
      },
      imei: 'IMEI',
      iccid: 'SIM ICCID',
      bluetooth: 'Bluetooth',
      wifiMac: 'WiFi MAC',
      marketplace: 'Market',
      manufacturer: 'Manufacturer',
      statusColumn: 'Fleet registration',
      attemptCount: '{count} attempt(s)',
      where: 'Accepted at',
      acceptedAt: 'Accepted',
      syncedAt: 'Sent',
      notSynced: 'Not yet',
      unsynced: '{count} of the modules listed are not in the fleet yet',
      withheldCount: '{count} held back until inspected',
      empty: 'No modules yet. One appears when a module passes incoming inspection.',
      loadFailed: 'Could not load the modules.',
      retry: 'Try again',
      actions: 'Actions',
      codesTitle: 'Module {imei}',
      command: {
        open: 'Commands',
        title: 'Command module {imei}',
        warningTitle: 'This reaches the real module',
        warningBody:
          'Each button sends the command straight to the module, and a module that has been built into a vehicle moves the vehicle. Check the vehicle below is the one you mean before pressing anything.',
        target: 'What this reaches',
        resolving: 'Looking it up…',
        notBoundYet: 'The module on its own — no vehicle has been built around it yet',
        resolveFailed: 'Could not work out what this module reaches.',
        sendFailed: 'The command could not be sent.',
        tabCommands: 'Commands',
        tabState: 'Read state',
        opsHint: 'Needs OPS mode. Send Enter OPS first.',
        send: 'Send',
        cancel: 'Cancel',
        confirmTitle: 'Send {command}?',
        confirmBody: 'This changes how the module runs. Check the target above first.',
        stateHint: 'Asks the module to re-send its state. Changes nothing, but reading the ECU powers it on.',
        answered: 'Answered',
        partial: 'Partial',
        noAnswer: 'No answer',
        readFailed: 'The state could not be read.'
      },
      admit: {
        open: 'Admit from a list',
        title: 'Admit modules from a list',
        warningTitle: 'These modules will not be inspected',
        warningBody:
          'Every module in the list becomes buildable into a vehicle without having been measured, and is held back from the fleet until someone inspects it. Nothing here records a test result.',
        fileLabel: 'List of IMEIs',
        choose: 'Choose a file',
        fileTip:
          'An .xlsx or .csv file. Any cell that is fifteen digits is read as an IMEI, so the columns and the header can be anything. If a column shows something like 8.7E+14, set it to text and export the file again — those digits are already lost.',
        reasonLabel: 'Why these may be built without an inspection',
        reasonPlaceholder: 'For example: which programme these are for, and what is not ready yet',
        submit: 'Admit them',
        close: 'Close',
        imported: 'Admitted',
        alreadyAccepted: 'Already inspected',
        duplicate: 'Already admitted',
        skipped: 'Rows with no IMEI',
        rejected: '{count} cell(s) looked like an IMEI and were not'
      },
      manual: {
        open: 'Add by hand',
        title: 'Add modules by hand',
        imeiLabel: 'IMEIs',
        imeiPlaceholder: 'One per line',
        imeiTip:
          'Fifteen digits each — the same rule the uploaded list uses. One per line, or separated by commas or spaces. Anything that is not an IMEI is shown back rather than skipped, so a mistyped module cannot be lost.',
        counted: '{valid} to add, {repeated} repeated',
        invalid: '{count} entr(y/ies) are not an IMEI',
        tooMany: 'That is more than can be added by hand. Upload it as a list instead.',
        submit: 'Add them'
      }
    },
    standards: {
      title: 'CCU EOL acceptance standards',
      description:
        'What counts as acceptable for a CCU, per production batch: which checks run and the rule each one is judged by. A batch owns its standard, so there is nothing shared between batches. Limits can be edited and apply from the next test onwards — every result already recorded keeps a copy of the rule it was judged against.',
      new: 'New batch',
      search: 'Search',
      searchPlaceholder: 'Search batch code or description',
      filterAll: 'Any state',
      filterAnyItem: 'Any check',
      enabled: 'Enabled',
      disabled: 'Disabled',
      batchCode: 'Batch code',
      batchDescription: 'Description',
      noDescription: 'No description',
      itemCount: 'Checks',
      itemCountValue: '{count} checks',
      state: 'State',
      lastEdited: 'Last edited',
      actions: 'Actions',
      edit: 'Edit',
      enable: 'Enable',
      disable: 'Disable',
      empty: 'No batches yet. Create one to give a station something to test against.',
      loadFailed: 'Could not load the batches.',
      retry: 'Try again',
      createTitle: 'New batch',
      editTitle: 'Edit batch',
      save: 'Save',
      cancel: 'Cancel',
      batchCodePlaceholder: '20260825-A',
      batchCodeHint:
        'What a station presents to fetch this configuration. It can still be corrected until the first module is tested.',
      batchCodeLockedHint:
        'Locked: {count} acceptance attempts already refer to this batch, and stations fetch their configuration by this code. Create another batch instead — you can copy this standard into it.',
      batchDescriptionPlaceholder: 'CCU5, first production run',
      items: 'The standard',
      itemsHint:
        'Every check this service can run on a CCU is listed. Untick the ones this batch does not check — {count} are ticked. The order here is the order stations show and run them in; move a row with the arrows.',
      item: 'Check',
      itemWithdrawn: 'Withdrawn',
      noItemDescription: 'No description in the catalogue yet.',
      ruleType: 'Rule',
      ruleValue: 'Value',
      example: 'Example',
      checked: 'Checked',
      min: 'Lower',
      max: 'Upper',
      valuePlaceholder: 'The exact text',
      patternPlaceholder: 'A regular expression',
      noValueNeeded: 'No value needed',
      catalogueUnavailable: 'The check catalogue could not be loaded, so a standard cannot be configured now.',
      ruleTypeName: {
        EXACT: 'Equals',
        REGEX: 'Matches',
        RANGE: 'Range',
        NONE: 'Record only'
      },
      importFrom: 'Import from a batch',
      import: 'Import',
      imported: 'Imported {count} rules from {code}.',
      checkAll: 'Check all',
      uncheckAll: 'Uncheck all',
      catalogueOrder: 'Default order',
      order: 'Order',
      moveUp: 'Move up',
      moveDown: 'Move down',
      issueCount: '{count} checks are not filled in yet',
      judgesNothing:
        'Every check is set to record only, so this batch judges nothing and would pass every module. That is a valid pilot batch — worth knowing it is what you are saving.',
      itemRule: {
        natsLeaf:
          'The URL a module of this batch must be found dialling; the station reads it and never changes it. Use Equals with nats-leaf://host:port — no path, no user, nothing after the port.',
        natsUplink:
          'Use Equals with connected, or Record only. Needs nats.leaf on this batch, and fails every module against a hub that does not yet admit it.',
        ccuPoweroff:
          'Use Equals with off, or Record only. The station powers the module off last, and only when everything else passed.',
        ccuProvision:
          'Set it by the batch’s settings: fuse and close → Equals CLOSED; fuse only → Equals LOCKED; not fused here → leave it out or Record only, or Equals OPEN if boards must stay unfused. FUSED (fused, not locked) is a defect and never a passing value. Needs ZestOS ccu5-v0.4.0 or later.',
        ccuOs:
          'The ZestOS release this batch ships with. Use Equals with the release as the module reports it, v included — v0.4.2, not 0.4.2.',
        lteFw:
          'The LTE modem firmware the line upgrades to. Use Equals with the full revision, for example EG916QGLLGR01A05M04_A0.305.A0.305, the part after the underscore included. Equals on the part before it fails every CCU; a pattern that matches only that part passes a CCU that missed the upgrade.'
      },
      issue: {
        valueRequired: 'needs the text it must equal.',
        patternRequired: 'needs a pattern to match.',
        patternInvalid: 'the pattern is not a valid regular expression.',
        boundsRequired: 'needs a lower and an upper bound.',
        boundsNotNumeric: 'the bounds have to be numbers.',
        boundsInverted: 'the lower bound is greater than the upper one.',
        leafUrlInvalid: 'has to be Equals, with a URL of the form nats-leaf://host:port.',
        uplinkNeedsLeaf: 'can only be checked when nats.leaf is checked too.'
      },
      batchCodeRequired: 'A batch code is required.',
      itemsRequired:
        'A batch has to check something. An enabled batch that checks nothing would pass every module.',
      fixIssuesFirst: 'Some checks are not filled in. Fix them before saving.',
      saved: 'Saved.',
      disableConfirmTitle: 'Disable batch',
      disableConfirm:
        'Disable {code}? Stations will refuse to test it rather than fall back to anything, and the records already taken against it stay.',
      enabledDone: 'The batch is enabled.',
      disabledDone: 'The batch is disabled.',
      appliesFromNextTest: 'Applies from the next test onwards.',
      pastRecordsKeepTheirRule:
        'The {count} attempts already recorded keep the rule they were judged against.',
      lastEditedBy: 'Last edited by {who} at {when}.',
      settings: 'Settings',
      settingsHint:
        'How a station runs this batch, rather than what passes. Saved with the batch; a setting you leave as it is is not changed.',
      settingDefault: 'Default',
      settingsNotLoaded: 'The settings have not loaded yet, so the batch is not saved. Wait a moment, or reopen the form.',
      settingValue: {
        'provision.mode': {
          NONE: 'Not fused here',
          FUSE: 'Fuse only',
          FUSE_AND_CLOSE: 'Fuse and close'
        }
      },
      closeConfirmTitle: 'Close boards in this batch',
      closeConfirm:
        'From now on every board a fuse station fuses in {code} is also closed. Closing cannot be undone: a closed board can no longer be read or fused over USB. Save it?',
      closeConfirmOk: 'Close boards in this batch'
    }
  },
  vehicleEol: {
    checkItems: {
      title: 'Vehicle check items',
      subtitle: 'What can be checked on a whole vehicle.',
      note: 'Read-only. An item code is what the station tool and the app report against, so the set arrives with a release rather than being created here. Which of these a SKU is judged by, and to what limit, is an acceptance standard.',
      code: 'Item',
      name: 'Name',
      component: 'Component',
      wholeVehicle: 'whole vehicle',
      testType: 'Test type',
      description: 'What it reads',
      state: 'State',
      inUse: 'In use',
      withdrawn: 'Withdrawn',
      anyCollectedBy: 'Read from the vehicle or by a person',
      collectedBy: 'Collected by',
      operatorCount: '{count} need a person',
      anyTestType: 'Any test type',
      anyComponent: 'Any component',
      empty: 'No check items match.',
      loadFailed: 'The check items could not be loaded.',
      operatorInstruction: 'Instruction (source text)',
      instructionMissing: 'No instruction',
      instructionNotNeeded: 'not needed',
      missingInstructionCount: '{count} operator checks have no instruction',
      instructionNote: 'The station app shows these words in the operator\u2019s own language, keyed on the item code \u2014 what is here is the English they are written from, and the fallback for a code the app has no string for. Correcting an operator\u2019s wording is a station release. What matters on this page is whether a check has an instruction at all: without one the app shows no action step.'
    },
    testType: {
      STATIC: 'Static inspection',
      RIDE: 'Ride test'
    },
    collectedBy: {
      VEHICLE: 'Read from the vehicle',
      OPERATOR: 'Operator acts on the vehicle'
    },
    binding: {
      title: 'Vehicle bindings',
      description:
        'Which CCU is fitted in which frame, what plate is on it, and which VIN it carries — plus how far telling the ops platform has got. A binding is normally made and released at a station with the vehicle in hand; binding from here is for correcting one after the vehicle has left that bench.',
      keywordPlaceholder: 'Frame number, plate, IMEI or VIN',
      filterAnyState: 'Live and released',
      filterAnyStatus: 'Any sync state',
      search: 'Search',
      frameNumber: 'Frame number',
      plateNumber: 'Plate',
      imei: 'CCU IMEI',
      vinCode: 'VIN',
      noVin: 'not carried',
      qrCode: 'Vehicle QR code',
      scanTitle: '{label} · {frame}',
      scanCue: 'Click a code to show it as a symbol a scanner can read.',
      qrHint: 'This holds the URL the code on the vehicle encodes, not the plate.',
      releasedAt: 'Released',
      opsBikeId: 'Ops vehicle id',
      sku: 'SKU',
      opsStatusColumn: 'Vehicle sync',
      vinStatusColumn: 'VIN sync',
      where: 'Bound at',
      boundAt: 'When',
      attemptCount: '{count} attempts',
      unsynced: '{count} on this page not yet sent',
      empty: 'No bindings match.',
      loadFailed: 'The bindings could not be loaded.',
      retry: 'Try again',
      actions: 'Actions',
      cancel: 'Cancel',
      bind: {
        open: 'Bind a vehicle',
        title: 'Bind a vehicle',
        notice:
          'This writes the same record a station writes, under the same checks. Use it to correct a vehicle that has left the bench, not to do the bench\u2019s work.',
        optionsFailed: 'The factory and SKU lists could not be loaded.',
        factory: 'Factory',
        factoryHint:
          'Where this vehicle was built. The frame number\u2019s factory characters are checked against it, so the wrong one refuses the code rather than filing it in the wrong place.',
        sku: 'SKU',
        qrCode: 'Vehicle QR code',
        qrHint: 'The URL the code on the plate encodes, not the six printed characters.',
        resolvedPlate: 'That code is the plate {plate}. Check it against the vehicle.',
        vinHint: 'This SKU carries a VIN, so one is required.',
        checking: 'Checking\u2026',
        submit: 'Bind',
        confirmTitle: 'Bind this vehicle?',
        confirmBody:
          'Frame {frame}, plate {plate}, CCU {imei}, at {factory}. The ops platform will be told about it.',
        done: 'Bound {frame}.',
        failed: 'Nothing was bound.'
      },
      release: {
        open: 'Release',
        title: 'Release this binding?',
        body:
          'Frame {frame} and its plate, CCU and VIN are handed back for another vehicle to claim, and the ops platform is told the vehicle has gone. The record is kept.',
        reason: 'Why',
        reasonHint: 'The only trace of why this vehicle was reworked. Somebody will read it months from now.',
        submit: 'Release',
        done: 'Released {frame}.',
        failed: 'Nothing was released.'
      },
      state: {
        LIVE: 'Live',
        RELEASED: 'Released'
      },
      operation: {
        UNBIND: 'removal'
      },
      status: {
        PENDING: 'Waiting to send',
        IN_PROGRESS: 'Sending',
        SYNCED: 'Sent',
        FAILED: 'Send failed',
        CANCELLED: 'Cancelled'
      }
    },
    acceptanceRecords: {
      title: 'Vehicle EOL acceptance records',
      description:
        'One row per attempt at one of the two vehicle inspections. Each has its own attempt sequence, so a static retest leaves the ride history alone and first pass yield stays computable for each. Expand a row for every check, the value that was reported, and the rule it was judged against at that moment.',
      search: 'Search',
      framePlaceholder: 'Frame number starts with',
      imeiPlaceholder: 'IMEI starts with',
      filterAnyTestType: 'Any test',
      filterAnyResult: 'Any result',
      filterAnySku: 'Any SKU',
      firstAttemptOnly: 'First attempts only',
      frameNumber: 'Vehicle',
      sku: 'SKU',
      testTypeColumn: 'Test',
      attemptNo: 'Attempt',
      resultColumn: 'Result',
      failedItems: '{count} failed',
      standard: 'Judged under',
      standardMissing: 'Standard missing',
      version: 'version {version}',
      where: 'Where and who',
      startedAt: 'Started',
      finishedAt: 'Finished',
      unfinished: 'Not finished',
      context: 'How this attempt was run',
      scanned: 'What was scanned',
      communicationPath: 'Reached over',
      toolVersion: 'Build',
      failureCode: 'Failure code',
      checks: 'Checks',
      item: 'Check',
      component: 'Component',
      wholeVehicle: 'whole vehicle',
      rawValue: 'Reported',
      noValue: 'No value obtained',
      judgedAgainst: 'Judged against',
      execution: 'Read',
      itemVerdict: 'Verdict',
      byOperator: 'confirmed by operator',
      checkTheVehicle: 'The operator acted and the vehicle did not respond. Check that the vehicle scanned is the one in front of them before sending anything to rework.',
      attempts: 'This vehicle at this test — {count} attempts',
      detailLoading: 'Loading…',
      detailFailed: 'This attempt could not be loaded.',
      empty: 'No attempts match.',
      loadFailed: 'The acceptance records could not be loaded.',
      retry: 'Try again',
      firstAttemptFailures: '{count} of the attempts shown are first attempts that did not pass',
      testType: {
        STATIC: 'Static inspection',
        RIDE: 'Ride test'
      },
      result: {
        PASS: 'Pass',
        FAIL: 'Fail',
        ABORTED: 'Cancelled',
        INFRA_ERROR: 'Test environment'
      },
      execution_: {
        OK: 'Read',
        TIMEOUT: 'No answer',
        ERROR: 'Error',
        SKIPPED: 'Not run'
      },
      itemVerdict_: {
        PASS: 'Pass',
        FAIL: 'Fail',
        NOT_JUDGED: 'Recorded'
      },
      rule: {
        exact: 'must be {value}',
        regex: 'must match {pattern}',
        versionList: 'one of {versions}',
        range: '{min} to {max}',
        atLeast: 'at least {min}',
        atMost: 'at most {max}',
        present: 'must answer',
        none: 'recorded only'
      },
      exchanges: 'Messages',
      viewExchanges: '{count} \u00b7 view',
      exchangesTitle: 'What {item} said to the vehicle',
      exchangesHint: 'The service\u2019s own account of the transport, in the order it happened: the command that made the check answerable, if it needed one, then the read. Evidence, not a verdict \u2014 a subject that did not arrive is a fact about a component and a refused publish is a fact about the network.',
      sent: 'sent',
      received: 'back',
      nothingSent: 'Nothing was sent.'
    },
    standards: {
      title: 'Vehicle acceptance standards',
      subtitle: 'What counts as an acceptable vehicle, one version at a time.',
      create: 'New standard',
      createTitle: 'New acceptance standard',
      editTitle: 'Edit {label}',
      supersedeTitle: 'New version, based on {label}',
      frozenNote: '{label} has already judged a vehicle, so it cannot be changed. Saving creates the next version, and the SKUs judged by this one keep it until someone chooses again.',
      code: 'Standard',
      codePlaceholder: 'e.g. SCOOTER_GEN5_EU',
      codeHint:
        'The code is the standard itself: every version saved under it keeps the code and takes the next number. A SKU is judged by one version, so pick a code once and reuse it.',
      description: 'Description',
      descriptionHint:
        'What this version requires, or what changed from the last one. It is the line shown when someone picks a standard for a SKU, so write it for whoever has to choose.',
      state: 'State',
      frozen: 'Settled',
      draft: 'Editable',
      frozenAt: 'First used',
      itemCount: 'Checks',
      skuCount: 'SKUs',
      actions: 'Actions',
      edit: 'Edit',
      supersede: 'New version',
      checks: 'Checks',
      checkedCount: '{count} selected',
      checksHint: 'Tick every check this version requires, then say how each is judged. NONE records the value without judging it, which is the safe default — a limit nobody chose is worse than no limit.',
      check: 'Check',
      testType: 'Test type',
      ruleType: 'Judged by',
      rule: 'Rule',
      withdrawn: 'withdrawn',
      noParameters: 'no parameters',
      valuePlaceholder: 'must equal',
      patternPlaceholder: 'regular expression',
      min: 'at least',
      max: 'at most',
      versionsPlaceholder: 'e.g. 3.8.1 — Enter to add',
      confirm: 'Operator confirms',
      codeRequired: 'A standard code is required.',
      cancel: 'Cancel',
      saveDraft: 'Save changes',
      saveAsNewVersion: 'Create version',
      savedDraft: 'Changes saved.',
      savedNewVersion: '{label} created.',
      empty: 'No standards yet.',
      loadFailed: 'The standards could not be loaded.',
      collectedBy: 'Collected by',
      collectedByHint: 'A check the operator collects is one the handheld prompts for; one the vehicle collects is read by the service. That follows from the source and is not set here.',
      operatorPresentWarning: 'This check is answered by the operator, and \u2018Answered at all\u2019 reads the status and not the answer \u2014 so \u2018it did not work\u2019 would pass. Use \u2018Exactly equals\u2019 with true.',
      delete: 'Delete',
      deleteConfirmTitle: 'Delete this standard?',
      deleteConfirm: '{label} and its checks will be removed. Nothing has been judged against it, so there is no record to keep \u2014 but a SKU that names it has to be pointed somewhere else first.',
      deleted: 'Standard deleted.',
      codeLockedHint: 'A later version cannot be renamed: v1 and v2 would keep the old code while this one moved. Only a first version\u2019s code can still be corrected.'
    },
    exchangeOutcome: {
      'answered': 'answered',
      'no answer in the window': 'no answer in the window',
      'NOT_SUPPORTED': 'nothing publishes this',
      'RATE_LIMITED': 'refused for volume',
      'CCU_SILENT': 'not asked: the CCU did not answer',
      'CCU_VERSION_UNREAD': 'not asked: the CCU did not report its firmware',
      'VENDOR_UNKNOWN': 'ECU maker unknown',
      'EPOCH_CHANGED': 'trip counter restarted mid-ride',
      'RESOLUTION_TOO_COARSE': 'trip counter too coarse',
      'TRANSPORT_UNAVAILABLE': 'could not reach vehicles'
    },
    ruleTypeHint: {
      NONE: 'Recorded and never judged. Use it to collect a value before anyone has agreed a limit for it.',
      PRESENT: 'Passes if the component answered at all. For communication checks: not answering means a missing part or a pinched harness.',
      EXACT: 'Passes if the value is exactly this. On an operator check the value is true.',
      REGEX: 'Passes if the value matches this pattern.',
      RANGE: 'Passes if the number falls inside these bounds. One bound is enough.',
      VERSION_LIST: 'Passes if the version is one of these. Approved builds are listed, never a range.'
    },
    ruleType: {
      NONE: 'Recorded only',
      PRESENT: 'Answered at all',
      EXACT: 'Exact value',
      REGEX: 'Pattern',
      RANGE: 'Number range',
      VERSION_LIST: 'Version list'
    }
  },
  admin: {
    organisations: {
      title: 'Factories',
      description:
        'Every factory whose units are accepted here, and Lime itself — which has no frame code because nothing is built there. There is no delete: disabling a factory stops everyone acting for it from signing in, while keeping its records auditable.',
      searchPlaceholder: 'Search code or name',
      search: 'Search',
      filterAll: 'All states',
      new: 'New factory',
      edit: 'Edit',
      enable: 'Enable',
      disable: 'Disable',
      code: 'Code',
      name: 'Name',
      frameCode: 'Frame code',
      vinSiteCode: 'VIN site code',
      latitude: 'Latitude',
      longitude: 'Longitude',
      coordinates: 'Coordinates',
      state: 'State',
      enabled: 'Enabled',
      disabled: 'Disabled',
      actions: 'Actions',
      createTitle: 'New factory',
      editTitle: 'Edit factory',
      save: 'Save',
      cancel: 'Cancel',
      codeRequired: 'A code is required.',
      nameRequired: 'A name is required.',
      disableConfirmTitle: 'Disable factory',
      disableConfirm: 'Disable {name}? Everyone acting for it will be unable to sign in until it is re-enabled.',
      saved: 'Saved.',
      enabledDone: 'Factory enabled.',
      disabledDone: 'Factory disabled.',
      empty: 'No factories. Run the migrations against this database.',
      loadFailed: 'Could not load factories.',
      retry: 'Try again',
      count: '{count} factories'
    },
    roles: {
      title: 'Roles',
      description:
        'What an operator may do. One role per account, and the role code is also the production step it works at — CCU_OUTGOING_EOL, VEHICLE_DYNAMIC_EOL. A role is never deleted and its code never changes, because every account points at it by that code.',
      searchPlaceholder: 'Search code or name',
      search: 'Search',
      filterAnyPermission: 'Any permission',
      new: 'New role',
      exportRoleReview: 'Export role design review',
      edit: 'Edit',
      code: 'Code',
      codePlaceholder: 'CCU_OUTGOING_EOL',
      codeHint: 'Upper case, digits and underscores. It cannot be changed afterwards.',
      codeImmutableHint:
        'A role code cannot be changed — every account holding this role points at it by this code. Create another role and move the accounts instead.',
      name: 'Name',
      permissions: 'Permissions',
      allPermissions: 'All permissions',
      noPermissions: 'Grants nothing yet',
      permissionsUnavailable: 'Could not load the permission list, so permissions cannot be edited now.',
      administratorLocked:
        'ADMINISTRATOR holds every permission, including any added later, and cannot be limited here — an edit that took it away would leave nobody able to administer anything.',
      renewalHint: 'Takes effect on an operator’s next token renewal, within the hour.',
      updatedAt: 'Updated',
      actions: 'Actions',
      createTitle: 'New role',
      editTitle: 'Edit role',
      save: 'Save',
      cancel: 'Cancel',
      codeRequired: 'A code is required.',
      codeInvalid: 'Use upper case letters, digits and underscores, starting with a letter.',
      nameRequired: 'A name is required.',
      saved: 'Saved.',
      empty: 'No roles. Run the migrations against this database.',
      loadFailed: 'Could not load roles.',
      retry: 'Try again'
    },
    operators: {
      title: 'Operators',
      description:
        'Accounts and roles for the people who sign in. Every verdict is attributed to one of them, so this is the audit trail for who was allowed to pass a unit. There is no delete — disabling an account blocks every sign-in method while keeping its records explainable.',
      searchPlaceholder: 'Search username or email',
      search: 'Search',
      filterAll: 'All states',
      filterAllOrganisations: 'All factories',
      filterAllRoles: 'All roles',
      new: 'New operator',
      import: 'Import from a file',
      exportAccessReview: 'Export user access review',
      edit: 'Edit',
      enable: 'Enable',
      disable: 'Disable',
      username: 'Username',
      role: 'Role',
      organisation: 'Factory',
      email: 'Email',
      signIn: 'Signs in with',
      signInPassword: 'Password',
      signInGoogle: 'Google',
      signInNone: 'No credential',
      credentials: 'Sign-in methods',
      addCredential: 'Add a sign-in method',
      credentialPassword: 'Password sign-in',
      credentialGoogle: 'Google sign-in',
      removeCredential: 'Remove',
      noCredentialWarning:
        'This account will have no way to sign in. That is allowed — an account can be created before its sign-in is decided — but nobody can use it until a method is added.',
      changePassword: 'Change',
      keepPassword: 'Keep current',
      newPassword: 'New password',
      confirmPassword: 'Confirm new password',
      passwordWillBeAdded: 'Set on save.',
      passwordWillBeReplaced: 'The stored password will be replaced on save.',
      passwordChangedAt: 'Set. Last changed {at} UTC.',
      passwordNeverChanged: 'Set. Never changed since the account was created.',
      passwordTooShort: 'A password must be at least {min} characters.',
      passwordMismatch: 'The two passwords do not match.',
      googleEmail: 'Google email',
      googleEmailHint: 'The work email a Google sign-in is matched on. Set it before the first sign-in.',
      googleEmailRequired: 'A Google email is required, or remove this sign-in method.',
      googleLinked: 'This account has signed in with Google.',
      googleNotLinked: 'Authorised, but nobody has signed in with Google yet.',
      state: 'State',
      enabled: 'Enabled',
      disabled: 'Disabled',
      actions: 'Actions',
      createTitle: 'New operator',
      editTitle: 'Edit operator',
      save: 'Save',
      cancel: 'Cancel',
      usernameRequired: 'A username is required.',
      roleRequired: 'A role is required.',
      organisationRequired: 'A factory is required.',
      disableConfirmTitle: 'Disable operator',
      disableConfirm:
        'Disable {username}? They will be unable to sign in by any method until the account is re-enabled.',
      saved: 'Saved.',
      enabledDone: 'Operator enabled.',
      lockedUntil: 'Locked until {at} UTC',
      lockedHint:
        'Too many wrong passwords in a row paused password sign-in. It unlocks on its own at this time, or now with Unlock.',
      unlock: 'Unlock',
      unlockedDone: 'Password sign-in unlocked.',
      disabledDone: 'Operator disabled.',
      empty: 'No operators match.',
      loadFailed: 'Could not load operators.',
      pickersFailed: 'Could not load the roles and factories to choose from.',
      retry: 'Try again',
      count: '{count} operators',
      importTitle: 'Import operators from a file',
      importIntro:
        'Two kinds of account, one file each. Pick the kind, choose the file, and every row is checked before anything is created — nothing is saved until you confirm.',
      importFactory: 'Factory accounts',
      importFactoryHint:
        'Accounts for a supplier factory, each signing in with a password and acting for the factory named in its row.',
      importInternal: 'Lime accounts',
      importInternalHint:
        'Accounts for Lime staff, each signing in with Google on a work email. All of them are under Lime, so the file does not carry a factory.',
      importColumns: 'Columns',
      importChooseFile: 'Choose a file',
      importTemplate: 'Download the template',
      importFormatsFailed: 'Could not load what the import file should look like.',
      importReadFailed: 'Could not read that file.',
      importAnotherFile: 'Choose another file',
      importStartOver: 'Start again',
      importChecking: 'Checking every row…',
      importReady: 'Every row can be created. Nothing has been saved yet.',
      importRejected:
        '{rejected} of {total} rows cannot be created. Nothing will be imported until they are corrected — an import lands whole or not at all.',
      importRowNumber: 'Line',
      importRowResult: 'Result',
      importRowOk: 'Will be created',
      importConfirm: 'Import {count} accounts',
      importDone: 'Imported {count} accounts.'
    }
  },
  tools: {
    /**
     * One entry per `ClientTool` constant. The backend serves the codes and never a label, so this is where a
     * program gets the name the factory calls it by.
     */
    name: {
      LIME_STATION_WINDOWS: 'Lime Station (Windows)',
      LIME_STATION_LINUX: 'Lime Station (Linux)',
      LIME_STATION_MACOS: 'Lime Station (macOS)',
      FACTORY_PDA_ANDROID: 'Lime Factory app'
    },
    description: {
      LIME_STATION_WINDOWS:
        'The workstation tool, on the PC at the bench. One program hosting the PC jobs: CCU end-of-line inspection, and CCU5 flashing.',
      LIME_STATION_LINUX:
        'The same workstation tool, built for a bench running Linux.',
      LIME_STATION_MACOS:
        'The same workstation tool, built for macOS — for development rather than for a line.',
      FACTORY_PDA_ANDROID:
        'Runs on a handheld PDA or a phone. Three-code binding, full inspection and container loading, in one app.'
    },
    platform: {
      WINDOWS: 'Windows PC',
      ANDROID: 'Android',
      LINUX: 'Linux PC',
      MACOS: 'macOS'
    },
    status: {
      DRAFT: 'Draft',
      PUBLISHED: 'Published',
      WITHDRAWN: 'Withdrawn'
    },
    downloads: {
      title: 'Downloads',
      subtitle: 'The software for your station, and the build to install.',
      empty: 'There is no station software for this account. Ask an administrator if you were expecting some.',
      loadFailed: 'The list of downloads could not be loaded.',
      noRelease: 'Nothing has been published for this yet.',
      mandatory: 'Required',
      version: 'Version',
      published: 'Published',
      size: 'Size',
      checksum: 'Checksum',
      download: 'Download {name}',
      started:
        'The download has started. Watch the downloads list or the notification bar, and open the installer from there when it finishes.',
      verifyHint:
        'The download comes through this console, so it may take a few minutes. The tool checks the file against the checksum above before installing.'
    },
    releases: {
      title: 'Releases',
      subtitle: 'Which build of each station program a factory installs.',
      create: 'New release',
      createTitle: 'New release',
      editTitle: 'Edit draft',
      tool: 'Program',
      toolPlaceholder: 'Choose a program',
      toolRequired: 'Choose which program this is a build of.',
      audience: 'Publishing it reaches every account that can {permission}.',
      version: 'Version',
      versionLabel: 'Version',
      versionPlaceholder: 'e.g. 1.4.2',
      versionRequired: 'A version is required, exactly as the program reports it.',
      versionHint:
        'Exactly the version the program reports about itself. Choosing a build below fills it in; typed by hand, copy it from the build rather than from memory.',
      versionFromPath: 'The build you chose sits in a folder named {version}.',
      versionMismatch:
        'This is not the folder the build sits in ({expected}). A version the program never reports about itself leaves every station being told it is out of date, on every check.',
      versionCode: 'Build number',
      versionCodeHint: 'Higher means newer. This is what decides it, not the version text.',
      versionCodeRequired: 'A build number of 1 or more is required.',
      versionCodeDerived: 'Worked out from version {version}: {code}. Change it if this build is numbered another way.',
      versionCodePrevious: 'The highest this program has so far is {code}, on version {version}.',
      versionCodeNotHigher:
        'Not higher than {highest}. A build numbered at or below the newest is offered to no station, and nothing reports an error.',
      mismatchConfirmTitle: 'Check this before saving',
      mismatchConfirmVersion: 'The version reads {typed}, but the build sits in a folder named {expected}.',
      mismatchConfirmBuild:
        'The build number {typed} is not higher than {highest}, which this program already has.',
      mismatchConfirmTail: 'Save it anyway?',
      saveAnyway: 'Save anyway',
      file: 'Installer',
      chooseFile: 'Choose file',
      noFile: 'No file chosen.',
      keepingFile: 'This build is {name}. To use a different file, discard this draft and upload again.',
      fileRequired: 'A new release needs an installer.',
      artifact: 'The build',
      sourceStorage: 'Already built',
      sourceUpload: 'Upload a file',
      sourceStorageHint:
        'Point at the build the pipeline produced for this program. Nothing is transferred through this page, and what gets published is what was built.',
      sourceUploadHint:
        'For a build the pipeline did not produce. The installer goes up through this page, which for a large one takes a while.',
      sourceKey: 'Build',
      sourceKeyPlaceholder: 'Browse, or paste the full path',
      browseNeedsTool: 'Choose the program first — you are shown its own builds.',
      sourceKeyRequired: 'Choose which build this release is made from.',
      browse: 'Browse',
      browseTitle: 'Choose a build',
      browseName: 'Name',
      browseSize: 'Size',
      browseModified: 'Built',
      browseEmpty: 'Nothing here.',
      browseMore: 'Show more',
      browseFailed: 'The list could not be loaded. You can still paste the full path.',
      urgency: 'Required',
      mandatory: 'Required',
      optional: 'Optional',
      mandatoryHint: 'On means a station may not keep working on an older build once this is published.',
      notes: 'What changed',
      notesPlaceholder: 'What an operator should know about this build.',
      draftHint: 'Saving creates a draft. Nothing reaches a factory until you publish it. The checksum is worked out from the file as it is stored, so there is nothing to fill in.',
      state: 'State',
      downloads: 'Downloads',
      publishedAt: 'Published',
      actions: 'Actions',
      edit: 'Edit',
      download: 'Download',
      publish: 'Publish',
      publishConfirmTitle: 'Publish this build?',
      publishConfirm: 'Every station running this program will be offered {version}.',
      publishMandatoryConfirm:
        'This build is marked required, so every station on an older one will be told to install {version} before it can carry on.',
      published: 'Published.',
      withdraw: 'Withdraw',
      withdrawConfirmTitle: 'Withdraw this build?',
      withdrawConfirm:
        'Version {version} stops being offered. It cannot be published again — release the next version instead.',
      withdrawn: 'Withdrawn.',
      discard: 'Discard',
      discardConfirmTitle: 'Discard this draft?',
      discardConfirm:
        'Version {version} and its installer are removed. It was offered to nobody, so nothing else changes.',
      discarded: 'Draft discarded.',
      saved: 'Saved as a draft.',
      cancel: 'Cancel',
      save: 'Save',
      search: 'Search',
      searchPlaceholder: 'Version or filename',
      allTools: 'Every program',
      allStatuses: 'Every state',
      allUrgencies: 'Required and optional',
      mandatoryOnly: 'Required only',
      optionalOnly: 'Optional only',
      empty: 'No releases yet. Upload a build and a factory has something to install.',
      loadFailed: 'The releases could not be loaded.',
      detailFailed: 'This release could not be loaded.',
      noNotes: 'Nothing was written about this build.',
      checksum: 'Checksum:',
      downloadsHeading: 'Downloads ({count})',
      noDownloads: 'Nobody has downloaded this build.',
      downloadedBy: 'Account',
      downloadedAt: 'When',
      downloadedFrom: 'From',
      downloadedWith: 'Program'
    }
  },
  firmware: {
    /** One entry per `FirmwarePackage` constant. The backend serves the codes and never a label. */
    name: {
      CCU5: 'CCU5'
    },
    status: {
      DRAFT: 'Draft',
      PUBLISHED: 'Published',
      WITHDRAWN: 'Withdrawn'
    },
    downloads: {
      title: 'Firmware download',
      subtitle: 'The firmware version you were sent a link to.',
      needLink: 'Open the link you were sent',
      needLinkDetail:
        'A firmware version is downloaded from the link in the email from Lime. If that link no longer works, ask Lime for a new one.',
      unavailable: 'This version cannot be downloaded',
      loadFailed: 'This firmware version could not be loaded.',
      noFiles: 'This version has no files yet.',
      published: 'Published',
      file: 'File',
      checksum: 'Checksum (SHA-256):',
      downloadNamed: 'Download {name}',
      verifyHint:
        'The download comes through this console, so a large package may take a few minutes. Compare the checksum above with the file you receive before flashing.'
    },
    releases: {
      title: 'Firmware releases',
      subtitle: 'Which firmware versions suppliers can download, and the files in each.',
      create: 'New version',
      createTitle: 'New firmware version',
      editTitle: 'Edit version',
      package: 'Package',
      packagePlaceholder: 'Choose a package',
      packageRequired: 'Choose which package this is a version of.',
      audience: 'Publishing it reaches every account that can {permission}.',
      version: 'Version',
      versionPlaceholder: 'e.g. v0.1.5',
      versionRequired: 'A version is required.',
      versionHint: 'As the firmware team named it. Each version of a package must be different.',
      versionFrozenHint: 'The version cannot change once it has been published. The notes can.',
      notes: 'What changed',
      notesPlaceholder: 'What a supplier should know about this version.',
      notesLiveHint: 'Suppliers see the new wording straight away.',
      draftHint:
        'Saving creates a draft with no files. Open its row to add the files, then publish. No supplier can see it until you do.',
      files: 'Files',
      filesHeading: 'Files ({count})',
      addFile: 'Add file',
      noFiles: 'No files yet. A version needs at least one before it can be published.',
      addFileTitle: 'Add a file',
      editFileTitle: 'Edit file',
      fileTitle: 'Title',
      fileTitlePlaceholder: 'e.g. Direct flash',
      fileTitleHint: 'What this file is for. The supplier reads it as the heading of the download.',
      fileTitleRequired: 'A title is required.',
      fileDescription: 'When to use it',
      fileDescriptionPlaceholder: 'When to use this file, and when not to.',
      fileWordsOnlyHint: 'Only the title and the description can change. To change the file itself, publish a new version.',
      artifact: 'The file',
      sourceUpload: 'Upload a file',
      sourceStorage: 'Already built',
      sourceUploadHint: 'The file goes up through this page, which for a large one takes a while.',
      sourceStorageHint: 'Point at a file a build has already written to storage. Nothing is transferred through this page.',
      sourceKey: 'Build',
      sourceKeyPlaceholder: 'Browse, or paste the full path',
      sourceKeyRequired: 'Choose which build this file is.',
      browse: 'Browse',
      browseTitle: 'Choose a build',
      browseName: 'Name',
      browseSize: 'Size',
      browseModified: 'Built',
      browseEmpty: 'Nothing here.',
      browseMore: 'Show more',
      browseFailed: 'The list could not be loaded. You can still paste the full path.',
      file: 'File',
      chooseFile: 'Choose file',
      noFile: 'No file chosen.',
      fileRequired: 'Choose a file to upload.',
      uploadFailed:
        'The file could not be uploaded to storage. Check the connection and try again; if it keeps failing, tell an administrator.',
      fileAdded: 'File added.',
      fileSaved: 'File saved.',
      fileRemoved: 'File removed.',
      removeFile: 'Remove',
      removeFileConfirmTitle: 'Remove this file?',
      removeFileConfirm: '{name} is removed from this draft. It was offered to nobody, so nothing else changes.',
      state: 'State',
      downloads: 'Downloads',
      publishedAt: 'Published',
      actions: 'Actions',
      copyLink: 'Copy link',
      linkCopied: 'Link copied. Paste it into the email to the supplier.',
      linkTitle: 'Link to send the supplier',
      close: 'Close',
      download: 'Download',
      edit: 'Edit',
      publish: 'Publish',
      publishConfirmTitle: 'Publish this version?',
      publishConfirm:
        'Every account that may download this package will be able to download {version}, with every file in it. After this its files and version cannot change. Versions already published stay available.',
      published: 'Published. Copy the link to send it to the supplier.',
      withdraw: 'Withdraw',
      withdrawConfirmTitle: 'Withdraw this version?',
      withdrawConfirm:
        'Version {version} can no longer be downloaded, including from links already sent. It cannot be published again.',
      withdrawn: 'Withdrawn.',
      discard: 'Discard',
      discardConfirmTitle: 'Discard this draft?',
      discardConfirm: 'Version {version} and its files are removed. It was offered to nobody, so nothing else changes.',
      discarded: 'Draft discarded.',
      saved: 'Draft created. Open its row to add the files.',
      edited: 'Saved.',
      cancel: 'Cancel',
      save: 'Save',
      search: 'Search',
      searchPlaceholder: 'Version or filename',
      allPackages: 'Every package',
      allStatuses: 'Every state',
      empty: 'No firmware versions yet.',
      loadFailed: 'The firmware versions could not be loaded.',
      detailFailed: 'This version could not be loaded.',
      noNotes: 'Nothing was written about this version.',
      downloadsHeading: 'Downloads ({count})',
      noDownloads: 'Nobody has downloaded this version.',
      downloadedBy: 'Account',
      downloadedFile: 'File',
      downloadedAt: 'When',
      downloadedFrom: 'From',
      downloadedWith: 'Program'
    }
  },
  stock: {
    title: 'Code issuance',
    description:
      'Frame numbers, VINs and plate QR codes — the codes a vehicle is built with. They are issued into the same pools the legacy factory tool draws from, because a code stamped into metal has to come from exactly one place.',
    appendOnly:
      'Issuing adds to a pool and nothing here can take anything back. A batch created by mistake stays in the pool, so check the model, the factory and the month before confirming.',
    search: 'Search',
    cancel: 'Cancel',
    retry: 'Retry',
    empty: 'Nothing issued yet.',
    loadFailed: 'Could not load what has been issued.',
    optionsFailed: 'Could not load the models and factories. Issuing is unavailable until this page is reloaded.',
    issueFailed: 'Nothing was issued.',
    exportFailed: 'Could not download the codes.',
    codePlaceholder: 'Code or pool',
    orderNoPlaceholder: 'Order number',
    anyModel: 'Any model',
    anyFactory: 'Any factory',
    anyMarket: 'Any market',
    pool: 'Pool',
    model: 'Model',
    factory: 'Factory',
    productMonth: 'Build month',
    order: 'Order',
    orderPlanned: '{quantity} planned',
    quantity: 'Count',
    range: 'First and last',
    issuedBy: 'Issued by',
    actions: 'Actions',
    export: 'Download',
    issuedFrame: 'Issued {quantity} frame numbers, {first} to {last}.',
    issuedVin: 'Issued {quantity} VINs, {first} to {last}.',
    confirm: {
      title: 'Issue this batch?',
      frameBody:
        'This will add {quantity} frame numbers for {model}, build month {month}, to the pool. It cannot be undone.',
      vinBody: 'This will add {quantity} VINs for {model}, build month {month}, to the pool. It cannot be undone.',
      issue: 'Issue'
    },
    form: {
      factory: 'Factory',
      frameFactoryHint: 'Where the frames are stamped. Only factories with a frame code can be chosen.',
      vinFactoryHint:
        'Where the vehicles are built. Only factories with a VIN site code can be chosen, and factories sharing a site code share a number range.',
      marketHint: 'Which market these vehicles are sold into. It is part of the VIN and cannot be changed afterwards.',
      monthHint: 'The month the vehicles will be built in. Not this month unless that is really the plan — stock is issued ahead of production, and the month decides which number range the batch draws from.',
      monthOutOfRange: 'Use a month close to now, written as 2026-09.',
      orderNo: 'Order number',
      orderNoHint: 'Optional, and recorded exactly as typed — nothing here checks it against an order. Leave it empty for a batch that belongs to none.',
      orderQuantity: 'Order quantity',
      orderQuantityHint: 'Optional. What the order plans, for the record. It does not limit how many are issued below.',
      quantityHint: 'Up to {max} in one batch.',
      quantityOutOfRange: 'Enter a count between 1 and {max}.',
      submit: 'Review'
    },
    codes: {
      open: 'Codes',
      frameTitle: 'Frame numbers in {pool}',
      vinTitle: 'VINs in {pool}',
      code: 'Code',
      claimLabel: 'Taken by',
      showSymbol: 'Show barcode',
      scanUrl: 'What the QR code encodes',
      scanUrlHint: 'The URL a station reads off the vehicle, not the plate',
      symbolTitle: 'Barcode for {code}',
      placeholder: 'Any part of a code',
      empty: 'No codes match.',
      loadFailed: 'Could not load the codes.',
      claim: {
        free: 'Nobody — free to use',
        boundHere: 'A vehicle bound here',
        takenInLegacy: 'The old system'
      }
    },
    frameBatches: {
      tab: 'Frame numbers',
      issue: 'Issue frame numbers'
    },
    vinBatches: {
      tab: 'VINs',
      issue: 'Issue VINs',
      market: 'Market'
    },
    qrImports: {
      tab: 'QR code files',
      import: 'Load a QR code file',
      upload: 'Load',
      placeholder: 'File name',
      codesTitle: 'QR codes from {file}',
      viewCodes: 'Codes',
      anyPosition: 'Any position',
      fileName: 'File',
      position: 'Position',
      positionHint: 'One file is one position on the vehicle, because each position carries a different code.',
      file: 'File',
      fileHint: 'A CSV with no heading row: the plate, a comma, then the address its code contains.',
      chooseFile: 'Choose a file',
      lines: 'Lines',
      imported: 'Loaded',
      duplicate: 'Already held',
      importedBy: 'Loaded by',
      skipped: 'Unreadable',
      done: 'Loaded {imported}. {duplicate} were already held, {skipped} could not be read.',
      failed: 'Nothing was loaded.'
    }
  },
  shipment: {
    line: 'Row',
    imei: 'IMEI',
    result: 'Result',
    stage: {
      OUTGOING: 'Outgoing',
      INCOMING: 'Incoming'
    },
    status: {
      PASSED: 'Passed',
      FAILED: 'Failed inspection',
      NOT_TESTED: 'Not tested',
      NOT_FOUND: 'No record',
      DUPLICATE: 'Duplicate in this list',
      INVALID: 'Not a valid IMEI'
    },
    statusHint: {
      OUTGOING: {
        PASSED: 'Cleared to ship.',
        FAILED: 'This CCU failed the outgoing inspection. Rework it and test it again.',
        NOT_TESTED: 'The outgoing inspection did not finish for this CCU. Test it again.',
        NOT_FOUND:
          'No outgoing inspection has been recorded for this IMEI. Test it before shipping, or check the IMEI.',
        DUPLICATE: 'This IMEI already appears on an earlier row. Remove the repeat.',
        INVALID:
          'An IMEI is exactly 15 digits. Look for a missing leading zero or a spreadsheet that reformatted the number.'
      },
      INCOMING: {
        PASSED: 'Accepted. It may be built into a vehicle.',
        NOT_FOUND:
          'This CCU has not been accepted: it has not passed the incoming inspection and was not admitted by Lime. Test it, or check the IMEI.',
        DUPLICATE: 'This IMEI already appears on an earlier row. Remove the repeat.',
        INVALID:
          'An IMEI is exactly 15 digits. Look for a missing leading zero or a spreadsheet that reformatted the number.'
      }
    },
    check: {
      title: 'CCU list check',
      description:
        'Upload a list of CCU IMEIs. An outgoing inspection account checks the list it is about to ship against the outgoing inspection records; an incoming inspection account checks the list it has received against the CCUs accepted for building into vehicles. Every IMEI that is not clear is listed.',
      chooseFile: 'Choose file',
      downloadTemplate: 'Download the template',
      fileHint:
        'An .xlsx or .csv file with one IMEI per row in the first column. A heading row reading IMEI or SN is optional. The template is the safest start: its IMEI column is formatted as text, so Excel does not turn an IMEI into scientific notation.',
      submit: 'Check list',
      chooseFirst: 'Choose a file first.',
      failed: 'The list could not be checked. Nothing was recorded.',
      allClear: 'All {count} IMEIs on this list passed the check.',
      notClear: '{problems} of {count} rows are not clear.',
      onlyPassed: {
        OUTGOING: 'Only IMEIs marked Passed may be shipped. Fix or remove the rest and check the list again.',
        INCOMING:
          'Only IMEIs marked Passed may be built into vehicles. Set the rest aside and check the list again once they are resolved.'
      },
      export: 'Export result',
      whatToDo: 'What to do'
    },
    manifests: {
      title: 'CCU list checks',
      description:
        'Every CCU list a factory has checked, outgoing and incoming, and what was found for each row. This is the record of what each factory said it shipped or received; factories do not see it.',
      fileNamePlaceholder: 'File name',
      operatorPlaceholder: 'Uploaded by',
      imeiPlaceholder: 'IMEI',
      anyStage: 'Outgoing and incoming',
      search: 'Search',
      retry: 'Retry',
      loadFailed: 'The checks could not be loaded.',
      itemsFailed: 'The rows of this check could not be loaded.',
      empty: 'No list has been checked yet.',
      itemsEmpty: 'No row matches.',
      file: 'File',
      stage: 'Checked as',
      where: 'Factory',
      lines: 'Rows',
      passed: 'Passed',
      toResolve: 'To resolve',
      checkedAt: 'Checked at (UTC)',
      open: 'Rows',
      anyStatus: 'Any result',
      attempt: 'Record',
      detailTotals: '{lines} rows, {problems} not clear'
    }
  },
  legacy: {
    title: 'Legacy factory codes',
    description:
      'The frame numbers, VINs and printed plate QR codes the legacy factory system has issued, and the vehicles it has already bound. Every code a binding accepts is issued upstream and this service cannot invent one, so a real code to test with comes from here — shown as the symbol its label carries, ready to scan off the screen.',
    readOnly:
      'These tables belong to the legacy factory system. This page only reads them; nothing here can change a row. Its idea of whether a code is free is its own — a code bound in this service still reads Free here, and only a binding check answers for both.',
    search: 'Search',
    symbolTitle: '{label} · {code}',
    scanCue: 'Click a code to show it as a symbol a scanner can read.',
    state: 'State',
    anyState: 'Any state',
    freeOnly: 'Not used yet',
    issuedAt: 'Issued',
    empty: 'Nothing matches.',
    loadFailed: 'Could not load this list.',
    retry: 'Try again',
    stockState: {
      FREE: 'Free',
      TAKEN: 'Used'
    },
    bindingState: {
      LIVE: 'Bound',
      RELEASED: 'Released'
    },
    frameNumbers: {
      tab: 'Frame numbers',
      frameNumber: 'Frame number',
      placeholder: 'Frame number starts with',
      label: 'RFID label',
      labelWritten: 'Written',
      labelNotWritten: 'Not written',
      codesTitle: 'Frame number {code}',
      codeHint: 'Stamped on the frame and read as a 1D barcode.'
    },
    vinCodes: {
      tab: 'VINs',
      vinCode: 'VIN',
      placeholder: 'VIN starts with',
      claimedBy: 'Claimed by frame',
      codesTitle: 'VIN {code}',
      codeHint: 'A VIN is claimed by a frame, so a used one is not available to another vehicle.'
    },
    qrCodes: {
      tab: 'Plate QR codes',
      plateNumber: 'Plate',
      scanUrl: 'What the QR code encodes',
      placeholder: 'Plate starts with',
      codesTitle: 'QR code {code}',
      positionColumn: 'Where it goes',
      positionUnknown: 'Not recorded',
      position: {
        plate: 'Plate',
        fender: 'Fender',
        down_tube: 'Down tube',
        Cart: 'Cart'
      },
      codeHint: 'A scan returns this URL, not the plate. The station tool sends it verbatim.'
    },
    bindings: {
      tab: 'Bound by the legacy tool',
      imei: 'Module',
      plateNumber: 'Plate',
      frameNumber: 'Frame number',
      skuCode: 'SKU',
      boundAt: 'Bound',
      imeiPlaceholder: 'IMEI starts with',
      framePlaceholder: 'Frame number starts with',
      platePlaceholder: 'Plate starts with',
      anyState: 'Bound and released',
      liveOnly: 'Bound only',
      vinCode: 'VIN',
      opsBikeId: 'Bike ID',
      notSynced: 'Not synced',
      plateQrCode: 'Plate QR code — {position}',
      codesTitle: 'Vehicle {plate}'
    }
  },
  /**
   * Wording for each permission the backend defines. The permission strings are the contract, so a code
   * with no entry here is shown raw rather than blank — see `permissionLabel` in RoleListView.
   */
  permission: {
    'acceptance:standard:read': 'Read acceptance standards',
    'acceptance:standard:write': 'Edit acceptance standards',
    'acceptance:standard:resolve': 'Fetch the standard in force (station tools)',
    'product:model:read': 'Read vehicle models',
    'product:model:write': 'Edit vehicle models',
    'product:sku:read': 'Read SKUs',
    'product:sku:write': 'Edit SKUs',
    'eol:record:read': 'Read acceptance records',
    'identity:ccu:read': 'Read CCU cloud identities',
    'identity:ccu:admit': 'Admit CCU modules without an inspection',
    'stock:code:read': 'Read what stock has been issued',
    'stock:code:issue': 'Issue frame numbers and VINs, and load QR codes',
    'shipment:ccu:check': 'Check a CCU list',
    'shipment:ccu:read': 'Read every CCU list check',
    'legacy:code:read': 'Browse the codes the legacy factory system holds',
    'tool:release:read': 'Read station software releases',
    'tool:release:write': 'Publish station software',
    'firmware:release:read': 'Read firmware releases',
    'firmware:release:write': 'Publish firmware',
    'firmware:ccu5:download': 'Download CCU5 firmware',
    'sop:ccu:outgoing': 'Read the CCU5 fuse & close and outgoing inspection SOP',
    'sop:ccu:incoming': 'Read the CCU incoming inspection SOP',
    'sop:vehicle:binding': 'Read the three-code binding SOP',
    'sop:vehicle:static': 'Read the vehicle static inspection SOP',
    'sop:vehicle:ride': 'Read the vehicle ride test SOP',
    'sop:shipment:ccu': 'Read the CCU list check SOP',
    'eol:ccu:test': 'Report a CCU acceptance attempt',
    'eol:vehicle:static': 'Run the static inspection',
    'eol:vehicle:ride': 'Run the ride test',
    'provision:ccu:read': 'Read what flash stations have done',
    'provision:ccu:flash': 'Flash a CCU board with production firmware',
    'provision:ccu:fuse': 'Fuse a CCU board\u2019s production key and close it',
    'ops:vehicle:command': 'Send a vehicle a command',
    'ops:vehicle:advanced': 'Send a vehicle advanced commands',
    'ops:vehicle:read': 'Read what has been sent to vehicles',
    'ops:ccu:bench': 'Work a CCU from a cabled workstation',
    'ops:ccu:deploy': 'Deploy a locally built firmware to a cabled CCU',
    'ops:chirp:mint': 'Mint the vehicle communication credential',
    'binding:vehicle:read': 'Read vehicle bindings',
    'binding:vehicle:write': 'Scan and bind a vehicle',
    'binding:vehicle:unbind': 'Release a vehicle binding',
    'binding:vehicle:console': 'Bind and release from the console',
    'iam:app:access': 'Sign in from the handheld app',
    'iam:pc:access': 'Sign in from the workstation tool',
    'iam:console:access': 'Sign in to the management console',
    'iam:operator:read': 'Read operator accounts',
    'iam:operator:write': 'Edit operator accounts',
    'iam:organisation:read': 'Read factories',
    'iam:organisation:write': 'Edit factories',
    'iam:role:read': 'Read roles',
    'iam:role:write': 'Edit roles and their permissions',
    'iam:audit:export': 'Download the user access and role design reviews'
  },
  /** A permission code without its action — the heading of one group on the roles page. */
  permissionResource: {
    'acceptance:standard': 'Acceptance standards',
    'product:model': 'Vehicle models',
    'product:sku': 'SKUs',
    'eol:record': 'Acceptance records',
    'identity:ccu': 'CCU cloud identities',
    'stock:code': 'Code issuance',
    'shipment:ccu': 'CCU lists',
    'legacy:code': 'Legacy factory codes',
    'tool:release': 'Station software',
    'firmware:release': 'Firmware releases',
    'firmware:ccu5': 'CCU5 firmware',
    'sop:ccu': 'CCU SOPs',
    'sop:vehicle': 'Vehicle SOPs',
    'sop:shipment': 'CCU list SOPs',
    'eol:ccu': 'CCU acceptance',
    'eol:vehicle': 'Vehicle acceptance',
    'provision:ccu': 'CCU provisioning',
    'ops:vehicle': 'Vehicle commands',
    'ops:ccu': 'CCU workstation',
    'ops:chirp': 'Vehicle credential',
    'binding:vehicle': 'Vehicle binding',
    'iam:app': 'Handheld app',
    'iam:pc': 'Workstation tool',
    'iam:console': 'Management console',
    'iam:operator': 'Operators',
    'iam:organisation': 'Factories',
    'iam:role': 'Roles',
    'iam:audit': 'Access reviews'
  },
  /** The last segment — the checkbox label inside a group, where the heading already says the resource. */
  permissionAction: {
    command: 'Send a command',
    export: 'Export',
    read: 'Read',
    write: 'Edit',
    resolve: 'Fetch (station)',
    test: 'Report',
    static: 'Static inspection',
    ride: 'Ride test',
    unbind: 'Unbind',
    check: 'Check a list',
    issue: 'Issue',
    mint: 'Mint',
    flash: 'Flash',
    fuse: 'Fuse and close',
    bench: 'Send a command',
    deploy: 'Deploy firmware',
    access: 'Sign in',
    download: 'Download',
    incoming: 'Incoming inspection',
    outgoing: 'Fuse & close and outgoing inspection',
    binding: 'Three-code binding'
  },
  help: {
    sops: {
      title: 'Standard operating procedures',
      description: 'Work instructions for the benches this account works at. Only documents that concern its permissions are listed.',
      empty: 'There are no work instructions for this account.',
      open: 'Read',
      documentNo: 'Document no.',
      version: 'Version',
      issued: 'Issued'
    },
    sop: {
      print: 'Print',
      back: 'Back to the list',
      notFound: 'There is no such SOP, or this account may not read it.',
      ccu5FuseOutgoing: {
        title: 'CCU5 fuse & close and outgoing inspection',
        description: 'The two Lime steps on a supplier line: fuse and close the bare board, then inspect the assembled module.'
      },
      ccu5Incoming: {
        title: 'CCU5 incoming inspection',
        description: 'At the vehicle factory: inspect each CCU5 as it arrives and register its identity before it is fitted.'
      },
      vehicleBinding: {
        title: 'Three-code binding',
        description: 'Tie the vehicle QR code, the frame number and the CCU IMEI into one record with the handheld.'
      },
      vehicleStatic: {
        title: 'Vehicle static inspection',
        description: 'Read what the assembled vehicle reports with the handheld, and complete the checks a person confirms.'
      },
      ccuListCheck: {
        title: 'CCU list check',
        description:
          'Before a CCU factory ships and after a vehicle factory receives, upload the list of CCU IMEIs to the console. Only CCUs marked "Passed" may be shipped or built into a vehicle.'
      },
      vehicleRide: {
        title: 'Vehicle ride test',
        description: 'Start a ride test on the handheld, ride as prompted, and wait for the backend’s verdict.'
      }
    }
  },
  forbidden: {
    heading: 'Not allowed',
    text: 'Your account does not have the role this page requires. Ask an administrator if you need it.'
  },
  error: {
    'identity.ccu.register.unavailable': 'The Chirp service account is not signed in and cannot sign in by itself here. Sign it in from the vehicle credential page first.',
    'identity.ccu.register.unreachable': 'The Chirp vehicle registry could not be reached. Nothing was changed; try again in a few minutes.',
    'identity.ccu.register.key_revoked': 'This key has been revoked in Chirp, which is permanent. This CCU cannot be registered again with it.',
    'identity.ccu.register.held_by_unknown_key': 'Chirp holds a different key for this IMEI, and it is not one this CCU ever reported. Nothing was changed; ask IoT which device it belongs to.',
    'identity.ccu.register.replace_required': 'Chirp still holds the key this CCU reported before. Registering the new key revokes the old one permanently.',
    'identity.ccu.register.key_on_another_imei': 'Chirp holds this key under a different IMEI. Nothing was changed.',
    'identity.ccu.register.not_allowed': 'The Chirp service account is not allowed to register real vehicles. Ask IoT to add it.',
    'identity.ccu.register.key_invalid': 'Chirp does not accept this as a public key. Re-test the CCU to read it again.',
    'identity.ccu.register.refused': 'The Chirp vehicle registry refused the registration. Nothing was changed.',
    'export.too_many_rows': 'More rows match these filters than one export carries. Narrow them and export again.',
    'shipment.manifest.file.empty': 'The file holds no IMEI. Check that the IMEIs are in the first column.',
    'shipment.manifest.file.unsupported': 'Only .xlsx and .csv files can be checked.',
    'shipment.manifest.file.unreadable': 'The file could not be read. Re-save it as .xlsx or .csv and upload it again.',
    'shipment.manifest.too_many_lines': 'This list has more rows than one check carries. Split it into several files.',
    'shipment.manifest.not_found': 'No such check.',
    'network.unreachable': 'Cannot reach the server.',
    'internal.error': 'Something went wrong on the server.',
    'auth.credentials.invalid': 'Incorrect username or password.',
    'auth.credentials.locked':
      'Too many incorrect passwords. Password sign-in for this account is paused for a while. Wait, or ask an administrator to unlock it.',
    'auth.required': 'Please sign in.',
    'auth.forbidden': 'You are not allowed to do that.',
    'auth.organisation.disabled': 'Your factory has been disabled, so sign-in is not available.',
    'auth.client.not_allowed': 'This account does not have access to the management console.',
    'iam.organisation.not_found': 'Factory not found.',
    'iam.organisation.code.duplicate': 'A factory with this code already exists.',
    'iam.organisation.frame_code.duplicate': 'A factory with this frame code already exists.',
    'iam.operator.not_found': 'Operator not found.',
    'iam.operator.username.duplicate': 'An operator with this username already exists.',
    'iam.operator.google_email.duplicate': 'Another operator is already authorised with this email.',
    'iam.operator.role.unknown': 'No role has this code.',
    'iam.operator.organisation.unknown': 'No factory has this code.',
    'iam.operator.self_disable': 'You cannot disable your own account.',
    'iam.operator.self_role_change': 'You cannot change the role of your own account.',
    'iam.operator.password.too_short': 'A password must be at least 14 characters.',
    'iam.operator.password.too_long':
      'This password is too long: at most 72 English letters or digits, or 24 Chinese characters.',
    'iam.operator.password.contains_username': 'A password must not contain the username.',
    'iam.operator.password.common':
      'This password is too common or too easy to guess. Use a longer phrase that is not built on a common word.',
    'iam.operator.password.reused': 'This account has used this password before. Choose a new one.',
    'iam.operator.import.malformed':
      'This file could not be read as CSV. A quote that is never closed is the usual cause.',
    'iam.operator.import.header':
      'The first row does not name the columns this kind of import expects. Check that the right kind was chosen, and download the template if in doubt.',
    'iam.operator.import.empty': 'This file has no rows below the header.',
    'iam.operator.import.too_many_rows': 'This file has more rows than one import carries. Split it.',
    'iam.operator.import.columns': 'This line does not have the same number of columns as the header.',
    'iam.operator.import.username.required': 'No username.',
    'iam.operator.import.username.duplicated': 'Another line in this file uses this username.',
    'iam.operator.import.role.required': 'No role.',
    'iam.operator.import.organisation.required': 'No factory.',
    'iam.operator.import.password.required': 'No password.',
    'iam.operator.import.email.required': 'No email address.',
    'iam.operator.import.email.invalid': 'This is not an email address.',
    'iam.operator.import.email.duplicated': 'Another line in this file uses this email address.',
    'iam.operator.import.value.too_long': 'A value on this line is longer than its column allows.',
    'iam.role.not_found': 'Role not found.',
    'iam.role.code.duplicate': 'A role with this code already exists.',
    'iam.role.code.immutable': 'A role code cannot be changed.',
    'iam.role.permission.unknown': 'That permission is not one this service defines.',
    'iam.role.permission.wildcard_reserved':
      'Every permission at once belongs to the ADMINISTRATOR role only.',
    'iam.role.administrator.wildcard_required':
      'The ADMINISTRATOR role must keep every permission, or nobody can administer anything.',
    'tool.release.not_found': 'That release no longer exists. Reload the list.',
    'tool.release.version.duplicate': 'Another build of this program is already that version.',
    'tool.release.version_code.duplicate': 'Another build of this program already has that build number.',
    'tool.release.not_draft':
      'This build has already been published, so it cannot be changed or discarded. Withdraw it and release the next version.',
    'tool.release.status.transition':
      'A draft is published, and a published build is withdrawn. Nothing goes back.',
    'tool.release.not_published': 'That build is not published, so it cannot be downloaded.',
    'firmware.release.not_found':
      'That firmware version cannot be found. If you followed a link, ask Lime for a new one; otherwise reload the list.',
    'firmware.release.version.duplicate': 'This package already has that version.',
    'firmware.release.not_draft':
      'That version has already been offered to a factory, so its files cannot change and it cannot be discarded. Withdraw it and publish a new version.',
    'firmware.release.version.frozen': 'A published version keeps its version number. Its notes can still change.',
    'firmware.release.withdrawn': 'That version has been withdrawn, so it can no longer be edited.',
    'firmware.release.no_artifacts': 'Add at least one file before publishing this version.',
    'firmware.artifact.not_found': 'That version has no such file. Reload and try again.',
    'firmware.artifact.name.duplicate': 'This version already has a file with that name.',
    'firmware.release.status.transition':
      'A draft is published, and a published version is withdrawn. Nothing goes back.',
    'firmware.release.not_published': 'That version has been withdrawn or is not published yet, so it cannot be downloaded.',
    unknown: 'Unexpected error.'
  },
  notFound: {
    heading: 'Page not found',
    back: 'Back to dashboard'
  },
  listExport: {
    button: 'Export',
    failed: 'Could not export the list. Try again.'
  },
  language: {
    label: 'Language',
    en: 'English',
    zh: '中文'
  }
}
